import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import withRouter from "Common/withRouter";
import { withTranslation } from "react-i18next";
import MenuData from "Layout/LayoutMenuData";
import { Menu, X, LogOut, ChevronDown, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { changeLeftsidebarSizeType } from "slices/thunk";
import img1 from "assets/images/logo/logo.png"; // مسیر لوگو را تنظیم کنید

const BASE_URL = "https://portal-api.mahanpanel.com";
// role_id: 0 = superadmin, 1 = admin, 2 = sales_expert, 3 = accounting_expert, 4 = sales_manager
const ROLE_NAMES: Record<number, string> = {
  0: "سوپرادمین",
  1: "ادمین",
  2: "کارشناس فروش",
  3: "کارشناس حسابداری",
  4: "مدیر فروش",
};

const ModernSidebar = (props: any) => {
  const menuData = MenuData();
  const dispatch = useDispatch<any>();
  // role_id لزوماً در payload توکن نیست، پس از GET /auth/me می‌خوانیم تا نقش واقعی کاربر
  // (نه یک متن ثابت) نمایش داده شود.
  const [roleId, setRoleId] = useState<number | null>(null);
  useEffect(() => {
    const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
    if (!token) return;
    const fetchMe = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRoleId(res.data?.role_id ?? null);
      } catch (error) {
        console.error("Error fetching current user role:", error);
      }
    };
    fetchMe();
  }, []);
  const isMobileOpen: boolean = props.isMobileOpen ?? false;
  const setIsMobileOpen: (value: boolean) => void =
    props.setIsMobileOpen ?? (() => { });
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const isCollapsed = props.layoutSidebarSizeType === "sm";
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef<HTMLDivElement>(null);

  // بستن سایدبار در موبایل هنگام تغییر مسیر
  useEffect(() => {
    setIsMobileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleCollapsed = () => {
    dispatch(changeLeftsidebarSizeType(isCollapsed ? "lg" : "sm"));
  };

  // تشخیص آیتم فعال
  const isActive = (link?: string) => {
    if (!link) return false;
    return location.pathname === link || location.pathname.startsWith(link + "/");
  };

  // بررسی فعال بودن زیرمنوها
  const isSubMenuActive = (subItems: any[]) => {
    return subItems?.some((item) => isActive(item.link)) || false;
  };

  // تغییر وضعیت باز/بسته منو
  const toggleMenu = (id: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // بستن همه منوها
  const closeAllMenus = () => {
    setExpandedMenus({});
  };

  // کلیک روی آیتم منو
  const handleMenuItemClick = (item: any, e: React.MouseEvent) => {
    if (item.subItems && item.subItems.length > 0) {
      if (isCollapsed) return; // در حالت بسته، زیرمنو باز نمی‌شود
      e.preventDefault();
      toggleMenu(item.id);
    } else if (item.link) {
      if (item.referens) {
        window.open(item.link, "_blank");
      } else {
        navigate(item.link);
      }
      closeAllMenus();
    }
  };

  // رندر آیتم منو به صورت بازگشتی
  const renderMenuItem = (item: any, level: number = 0) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isOpen = expandedMenus[item.id] || false;
    const active = isActive(item.link) || (hasSubItems && isSubMenuActive(item.subItems));
    const paddingLeft = level > 0 ? `pl-${4 + level * 3}` : "";

    const activeClasses = active
      ? "bg-red-500 text-white shadow-lg shadow-red-500/30"
      : "text-gray-700 hover:bg-sky-500/10 hover:text-red-600 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-red-400";

    return (
      <li
        key={item.id}
        className={`relative mb-0.5 group/item ${level === 0 ? "border-b border-slate-100/80 dark:border-white/5 last:border-b-0 pb-0.5" : ""
          }`}
      >
        <div
          className={`relative flex items-center ${isCollapsed ? "justify-center" : "justify-between"
            } px-4 py-2.5 rounded-xl cursor-pointer transition-all duration-300 group ${activeClasses} ${paddingLeft}`}
          onClick={(e) => handleMenuItemClick(item, e)}
        >
          <div className={`flex items-center ${isCollapsed ? "" : "gap-3"}`}>
            {item.icon && (
              <span className="transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[-2deg]">
                {React.cloneElement(item.icon, {
                  className: `w-5 h-5 ${active ? "text-white" : "text-gray-500 dark:text-gray-400"}`,
                })}
              </span>
            )}
            {!isCollapsed && (
              <span className="text-sm font-medium tracking-wide">{item.label}</span>
            )}
          </div>
          {hasSubItems && !isCollapsed && (
            <ChevronDown
              size={16}
              className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""
                } ${active ? "text-white" : "text-gray-400 dark:text-gray-500"}`}
            />
          )}
        </div>

        {/* تولتیپ حالت بسته: فقط نام منو روی هاور */}
        {isCollapsed && (
          <span className="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-3 whitespace-nowrap rounded-lg bg-gray-900 text-white text-xs font-medium px-3 py-1.5 shadow-lg opacity-0 scale-95 group-hover/item:opacity-100 group-hover/item:scale-100 transition-all duration-200 z-[1010] dark:bg-gray-700">
            {item.label}
            <span className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45" />
          </span>
        )}

        {/* زیرمنوها با انیمیشن ارتفاع (فقط در حالت باز) */}
        {hasSubItems && !isCollapsed && (
          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              maxHeight: isOpen ? "1000px" : "0",
              transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <ul className="mr-4 mt-1 space-y-0.5 border-r-2 border-red-400 dark:border-red-500/70">
              {item.subItems.map((subItem: any) => renderMenuItem(subItem, level + 1))}
            </ul>
          </div>
        )}
      </li>
    );
  };

  return (
    <>
      {/* دکمه همبرگر برای موبایل */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed top-4 right-4 z-[1001] w-12 h-12 rounded-2xl bg-white/80 backdrop-blur-xl shadow-lg flex items-center justify-center border border-sky-100 dark:bg-gray-800/70 dark:border-gray-700/50 transition-all hover:scale-105 active:scale-95"
      >
        <Menu size={24} className="text-gray-700 dark:text-gray-200" />
      </button>

      {/* سایدبار اصلی */}
      <div
        ref={sidebarRef}
        className={`app-menu fixed top-0 right-0 h-screen z-[1003] flex flex-col transition-all duration-300 ease-in-out
          bg-sky-50/70 backdrop-blur-xl
          max-lg:bg-gradient-to-b max-lg:from-white max-lg:via-sky-50/95 max-lg:to-sky-100/90
          dark:bg-gray-900/40 dark:backdrop-blur-xl dark:bg-none
          border-l border-slate-300 dark:border-slate-500
          shadow-[0_8px_40px_-8px_rgba(56,120,200,0.25)] dark:shadow-black/20
          ${isCollapsed ? "w-20" : "w-64"}
          ${isMobileOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"}`}
      >
        {/* هدر */}
        <div className="relative px-4 pb-4 border-b border-sky-100 dark:border-gray-700/40 bg-gradient-to-br from-sky-50/60 to-white/40 dark:from-red-900/10 dark:to-rose-900/10">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-14 h-14 shrink-0 rounded-xl flex items-center justify-center   bg-transparent overflow-hidden">
                <img src={img1} alt="ماهان پانل" className="w-full h-full object-contain" />
              </div>
              {!isCollapsed && (
                <div>
                  <h1 className="text-lg font-bold bg-gradient-to-r from-green-600 to-green-600 bg-clip-text text-transparent whitespace-nowrap">
                    <span className="text-black dark:text-green-500 ">ماهان {""}</span>
                    پانل
                  </h1>
                  {/* <span className="text-sm">                  انتخاب اول درصنعت ساندویچ پانل
                  </span> */}
                </div>
              )}
            </Link>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="lg:hidden p-1 rounded-lg hover:bg-sky-100/60 dark:hover:bg-white/10 transition-colors"
            >
              <X size={20} className="text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>

        {/* پروفایل کاربر */}
        {!isCollapsed && (
          <div className="mx-4 mt-4 p-3 bg-white/60 dark:bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-sky-100 dark:border-gray-700/20 flex items-center gap-3 transition-all hover:bg-white/80 dark:hover:bg-gray-800/50">
            <div className="w-9 h-9 bg-red-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-red-500/20">
              ا
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-800 dark:text-white">ماهان پانل</h4>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {roleId !== null && ROLE_NAMES[roleId] ? ROLE_NAMES[roleId] : "-"}
              </p>
            </div>
          </div>
        )}

        {/* منوها */}
        <div className="flex-1 overflow-y-auto py-3 px-3 custom-scrollbar">
          <ul className="space-y-1">{menuData.map((item: any) => renderMenuItem(item))}</ul>
        </div>

        {/* خروج */}
        <div className="p-3 border-t border-sky-100 dark:border-gray-700/20 bg-gradient-to-t from-sky-50/30 to-transparent dark:from-red-900/5">
          <button
            onClick={() => {
              localStorage.removeItem("app_at_mahan");
              localStorage.removeItem("user_role");
              window.location.href = "/login";
            }}
            className={`w-full flex items-center ${isCollapsed ? "justify-center" : "gap-3"
              } px-4 py-2.5 rounded-xl text-red-500 dark:text-red-400 hover:bg-red-50/60 dark:hover:bg-red-900/20 transition-all duration-300 group backdrop-blur-sm hover:shadow-lg hover:shadow-red-500/10`}
          >
            <LogOut size={18} className="transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[-5deg]" />
            {!isCollapsed && <span className="text-sm font-medium">خروج از سیستم</span>}
          </button>
        </div>

        {/* دکمه باز/بسته کردن منو - پایین سایدبار */}
        <div className="p-3 pt-0 border-t border-sky-100/70 dark:border-gray-700/20 hidden lg:block">
          <button
            onClick={toggleCollapsed}
            className={`w-full flex items-center ${isCollapsed ? "justify-center" : "gap-3"
              } px-4 py-2.5 mt-3 rounded-xl bg-white/60 dark:bg-gray-800/40 border border-sky-100 dark:border-gray-700/30 text-gray-600 dark:text-gray-300 hover:bg-sky-100/60 dark:hover:bg-gray-800/70 hover:text-red-600 dark:hover:text-red-400 transition-all duration-300 shadow-sm`}
          >
            {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            {!isCollapsed && <span className="text-sm font-medium">بستن منو</span>}
          </button>
        </div>
      </div>

      {/* اوورلی موبایل */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[1002] lg:hidden transition-opacity duration-300"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* استایل‌های سفارشی */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #C0151B;
          border-radius: 20px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #C0151B;
        }
        @media (max-width: 1023px) {
          .app-menu {
            transform: translateX(${isMobileOpen ? "0" : "100%"});
            transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
        }
      `}</style>
    </>
  );
};

export default withRouter(withTranslation()(ModernSidebar));